// QRValidator.jsx - Place this in your Components folder (src/Components/QRValidator.jsx)

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../Styles/QRValidator.css';

const QRValidator = () => {
  const { combinedCode } = useParams();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [tableInfo, setTableInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Parse the combined code to extract restaurantId and qrCode
  let restaurantId, qrCode;
  if (combinedCode && combinedCode.includes('-')) {
    const lastHyphenIndex = combinedCode.lastIndexOf('-');
    restaurantId = combinedCode.substring(0, lastHyphenIndex);
    qrCode = combinedCode.substring(lastHyphenIndex + 1);
  } else {
    // Fallback for old format (just QR code)
    qrCode = combinedCode;
    restaurantId = null;
  }

  useEffect(() => {
    const validateQRCode = async () => {
      try {
        console.log('Validating QR code:', { combinedCode, restaurantId, qrCode });

        // Clear any existing session first
        sessionStorage.removeItem('hasValidQR');
        
        // Use different API endpoint based on whether we have restaurantId
        const apiUrl = restaurantId 
          ? `https://waiter-ai.onrender.com/api/table/verify_table/${restaurantId}/${qrCode}`
          : `https://waiter-ai.onrender.com/api/table/verify_table/${qrCode}`;
        
        console.log('API URL:', apiUrl);
          
        const response = await fetch(apiUrl);
        
        // Check if response is ok before parsing
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        // Check multiple possible response formats
        const isValidResponse = data.valid === true || 
                               data.status === 'valid' || 
                               (data.table_info && Object.keys(data.table_info).length > 0);
        
        if (isValidResponse) {
          // Store validation data in session
          const tableData = data.table_info || data.table || { table_name: 'Unknown', party_size: 1 };
          
          sessionStorage.setItem('tableInfo', JSON.stringify(tableData));
          sessionStorage.setItem('qrCode', qrCode);
          if (restaurantId) {
            sessionStorage.setItem('restaurantId', restaurantId);
          }
          sessionStorage.setItem('hasValidQR', 'true');
          

          // Set states to show the welcome screen
          setTableInfo(tableData);
          setIsValid(true);
          setIsValidating(false);
        } else {
          // Invalid QR code
          setError('Invalid QR code');
          setIsValidating(false);
          setIsValid(false);
          
          // Wait before redirecting
          setTimeout(() => {
            navigate('/about_us');
          }, 2000);
        }
      } catch (error) {
        setError(error.message);
        setIsValidating(false);
        setIsValid(false);
        
        // Wait before redirecting
        setTimeout(() => {
          navigate('/about_us');
        }, 2000);
      }
    };

    // Only validate if we have both restaurantId and qrCode
    if (qrCode && restaurantId) {
      validateQRCode();
    } else {
      navigate('/about_us');
    }
  }, [combinedCode, navigate]);

  const handleStartOrdering = async () => {
    try {
      
      // Enable audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Request microphone permission
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Stop the stream immediately - we just needed to trigger permission
        stream.getTracks().forEach(track => track.stop());

      }


      const utterance = new SpeechSynthesisUtterance('');
      utterance.volume = 0;
      window.speechSynthesis.speak(utterance);
    
      // Mark audio as enabled
      sessionStorage.setItem('userHasInteracted', 'true');
      sessionStorage.setItem('audioEnabled', 'true');
      

      navigate('/', { replace: true });
    } catch (error) {
      navigate('/', { replace: true });
    }
  };

  // Show loading state
  if (isValidating) {
    return (
      <div className="qr-validator-container">
        <div className="validation-loading">
          <div className="spinner-qr"></div>
          <h2>PREPARING YOUR TABLE</h2>
        </div>
      </div>
    );
  }

  // Show error state
  if (!isValid && error) {
    return (
      <div className="qr-validator-container">
        <div className="validation-error-card">
          <div className="error-icon-qr">!</div>
          <h2>INVALID QR CODE</h2>
          <p>{error || 'This QR code is not recognized'}</p>
          <p className="redirect-text">Redirecting to help...</p>
        </div>
      </div>
    );
  }

  // Only show welcome screen if we have valid QR and not validating
  if (!isValid || !tableInfo) {
    return null; // Return nothing while states settle
  }

  // Show welcome screen for valid QR
  return (
    <div className="qr-validator-container">
      <div className="qr-welcome-card">
        <div className="restaurant-logo">🍽️</div>
        
        <h1 className="welcome-title-qr">WELCOME</h1>
        
        {tableInfo && (
          <div className="table-badge-qr">
            TABLE {tableInfo.table_name || 'N/A'}
          </div>
        )}
        
        <button
          onClick={handleStartOrdering}
          className="start-ordering-btn-qr"
        >
          <svg className="mic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
          START ORDERING
        </button>
        
        <p className="help-text-qr">
          You can order by voice or by tapping the menu
        </p>
      </div>
    </div>
  );
};

export default QRValidator;